import { useEffect, useMemo, useState } from "react";
import {
    Clock,
    AlertCircle,
    RefreshCw,
    Loader2,
    Check,
    Archive,
    Rocket,
    LifeBuoy,
    ChevronDown,
    ChevronUp,
    Square,
    CheckSquare,
} from "lucide-react";
import {
    adminFeedbackTriage,
    adminFeedbackPatch,
    adminFeedbackConvertTicket,
} from "@/lib/api";
import { toast } from "sonner";

const SOURCE_LABEL = {
    review: "Review",
    report_feedback: "Report",
    issue_feedback: "Issue",
    exposure_feedback: "Exposure",
    feature_request: "Feature request",
};

const SOURCE_FILTERS = [
    { key: "all", label: "All sources" },
    { key: "review", label: "Reviews" },
    { key: "feature_request", label: "Feature requests" },
    { key: "report_feedback", label: "Report" },
    { key: "issue_feedback", label: "Issue" },
    { key: "exposure_feedback", label: "Exposure" },
];

const SEGMENT_FILTERS = [
    { key: "", label: "All audiences" },
    { key: "first_time_buyer", label: "First-Time Buyer" },
    { key: "repeat_buyer", label: "Repeat Buyer" },
    { key: "homeowner", label: "Homeowner" },
    { key: "small_investor", label: "Small Investor (0–5)" },
    { key: "growing_investor", label: "Growing Investor (6–20)" },
    { key: "portfolio_investor", label: "Portfolio Investor (20+)" },
    { key: "investor", label: "Investor (unspecified)" },
    { key: "agent", label: "Agent" },
    { key: "property_manager", label: "Property Manager" },
    { key: "contractor", label: "Contractor" },
    { key: "inspector", label: "Inspector" },
    { key: "other", label: "Other" },
];

function snippet(item) {
    return (
        item.feedback_text ||
        item.comment ||
        item.request_text ||
        item.feedback_value ||
        "(no text)"
    );
}

function idOf(item, i) {
    return (
        item.review_id ||
        item.feedback_id ||
        item.issue_feedback_id ||
        item.exposure_feedback_id ||
        item.feature_request_id ||
        `idx-${i}`
    );
}

export default function FeedbackTriagePanel() {
    const [agingDays, setAgingDays] = useState(7);
    const [sourceFilter, setSourceFilter] = useState("all");
    const [segmentFilter, setSegmentFilter] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [expandedIds, setExpandedIds] = useState(() => new Set());
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const refresh = async (d = agingDays, seg = segmentFilter) => {
        setLoading(true);
        try {
            const r = await adminFeedbackTriage(d, 100, seg || null);
            setData(r);
            // Clear any stale selections from before refresh.
            setSelectedIds(new Set());
        } catch {
            toast.error("Could not load triage queue.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh(agingDays, segmentFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const visibleItems = useMemo(() => {
        if (!data) return [];
        if (sourceFilter === "all") return data.items;
        return data.items.filter((it) => it.source_type === sourceFilter);
    }, [data, sourceFilter]);

    const toggleExpand = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllVisible = () => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            const allOn = visibleItems.every((it, i) => next.has(idOf(it, i)));
            if (allOn) {
                visibleItems.forEach((it, i) => next.delete(idOf(it, i)));
            } else {
                visibleItems.forEach((it, i) => next.add(idOf(it, i)));
            }
            return next;
        });
    };

    // Per-row status patch. Optimistically removes the row so the queue
    // self-clears as admins act on items.
    const patchStatus = async (item, idx, newStatus, label) => {
        const id = idOf(item, idx);
        setBusyId(id);
        try {
            await adminFeedbackPatch(item.source_type, id, { status: newStatus });
            removeFromData([id], item.severity_hint === "high" ? 1 : 0);
            toast.success(`${label} · removed from triage`);
        } catch {
            toast.error("Couldn't update status — try refreshing.");
        } finally {
            setBusyId(null);
        }
    };

    const convertToTicket = async (item, idx) => {
        const id = idOf(item, idx);
        setBusyId(id);
        try {
            const r = await adminFeedbackConvertTicket(item.source_type, id);
            removeFromData([id], item.severity_hint === "high" ? 1 : 0);
            toast.success(
                `Support ticket created · ${r.ticket?.ticket_id || ""} — moved out of triage`,
            );
        } catch {
            toast.error("Couldn't convert to ticket — try refreshing.");
        } finally {
            setBusyId(null);
        }
    };

    const bulkClear = async (newStatus, label) => {
        if (selectedIds.size === 0) return;
        setBulkBusy(true);
        const targets = visibleItems
            .map((it, i) => ({ it, i, id: idOf(it, i) }))
            .filter(({ id }) => selectedIds.has(id));
        let ok = 0;
        let failed = 0;
        let highCleared = 0;
        await Promise.all(
            targets.map(async ({ it, id }) => {
                try {
                    await adminFeedbackPatch(it.source_type, id, { status: newStatus });
                    ok += 1;
                    if (it.severity_hint === "high") highCleared += 1;
                } catch {
                    failed += 1;
                }
            }),
        );
        removeFromData(
            targets
                .filter((_, k) => k < ok || failed === 0)
                .map((t) => t.id),
            highCleared,
        );
        setSelectedIds(new Set());
        setBulkBusy(false);
        if (failed === 0) {
            toast.success(`${label} ${ok} items`);
        } else {
            toast.warning(`${label} ${ok} ok · ${failed} failed`);
        }
    };

    const removeFromData = (ids, highCleared) => {
        setData((prev) => {
            if (!prev) return prev;
            const ids_set = new Set(ids);
            const items = prev.items.filter(
                (it, i) => !ids_set.has(idOf(it, i)),
            );
            return {
                ...prev,
                items,
                total: Math.max(0, (prev.total || 0) - ids.length),
                high_severity_count: Math.max(
                    0,
                    (prev.high_severity_count || 0) - (highCleared || 0),
                ),
            };
        });
    };

    const allVisibleSelected =
        visibleItems.length > 0
        && visibleItems.every((it, i) => selectedIds.has(idOf(it, i)));

    return (
        <div className="bl-card p-5" data-testid="feedback-triage-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[var(--bl-amber)]" />
                    <h3 className="font-display font-semibold text-base text-bl">
                        Aging feedback triage
                    </h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-bl-muted">
                        Older than
                        <select
                            value={agingDays}
                            onChange={(e) => {
                                const d = Number(e.target.value);
                                setAgingDays(d);
                                refresh(d, segmentFilter);
                            }}
                            className="ml-2 bg-bl-bg border border-bl rounded px-2 py-1 text-xs"
                            data-testid="triage-aging-select"
                        >
                            <option value={3}>3 days</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                        </select>
                    </label>
                    <label className="text-xs text-bl-muted">
                        Source
                        <select
                            value={sourceFilter}
                            onChange={(e) => setSourceFilter(e.target.value)}
                            className="ml-2 bg-bl-bg border border-bl rounded px-2 py-1 text-xs"
                            data-testid="triage-source-filter"
                        >
                            {SOURCE_FILTERS.map((s) => (
                                <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="text-xs text-bl-muted">
                        Audience
                        <select
                            value={segmentFilter}
                            onChange={(e) => {
                                const v = e.target.value;
                                setSegmentFilter(v);
                                refresh(agingDays, v);
                            }}
                            className="ml-2 bg-bl-bg border border-bl rounded px-2 py-1 text-xs"
                            data-testid="triage-audience-filter"
                            title="Filter to feedback from users with a specific audience segment. Anonymous feedback is excluded when this filter is active."
                        >
                            {SEGMENT_FILTERS.map((s) => (
                                <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                        </select>
                    </label>
                    <button
                        type="button"
                        onClick={() => refresh()}
                        disabled={loading}
                        className="bl-btn-ghost text-xs gap-1.5"
                        data-testid="triage-refresh"
                    >
                        {loading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3 w-3" />
                        )}
                        Refresh
                    </button>
                </div>
            </div>

            {data && (
                <div className="grid grid-cols-3 gap-3 mb-5">
                    <div className="text-center">
                        <div className="font-display font-semibold text-2xl text-bl">
                            {data.total}
                        </div>
                        <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                            Aged items
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="font-display font-semibold text-2xl text-[var(--bl-rust)]">
                            {data.high_severity_count}
                        </div>
                        <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                            High severity
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="font-display font-semibold text-2xl text-bl">
                            {data.aging_days}d
                        </div>
                        <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                            Threshold
                        </div>
                    </div>
                </div>
            )}

            {selectedIds.size > 0 && (
                <div
                    className="mb-4 flex items-center gap-3 px-3 py-2 rounded border border-bl-primary bg-[var(--bl-teal-tint)]"
                    data-testid="triage-bulk-bar"
                >
                    <CheckSquare className="h-3.5 w-3.5 text-bl-primary" />
                    <span className="text-xs font-medium text-bl">
                        {selectedIds.size} selected
                    </span>
                    <button
                        type="button"
                        disabled={bulkBusy}
                        onClick={() => bulkClear("reviewed", "Marked reviewed ·")}
                        className="bl-btn-ghost text-[11px] gap-1 px-2 py-1 ml-auto"
                        data-testid="triage-bulk-reviewed"
                    >
                        {bulkBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Check className="h-3 w-3" />
                        )}
                        Mark reviewed
                    </button>
                    <button
                        type="button"
                        disabled={bulkBusy}
                        onClick={() => bulkClear("archived", "Archived ·")}
                        className="bl-btn-ghost text-[11px] gap-1 px-2 py-1"
                        data-testid="triage-bulk-archive"
                    >
                        {bulkBusy ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Archive className="h-3 w-3" />
                        )}
                        Archive
                    </button>
                    <button
                        type="button"
                        onClick={() => setSelectedIds(new Set())}
                        className="text-[11px] text-bl-muted underline-offset-2 hover:underline"
                        data-testid="triage-bulk-clear"
                    >
                        Clear
                    </button>
                </div>
            )}

            {data && visibleItems.length === 0 && (
                <p
                    className="text-sm text-bl-muted text-center py-6"
                    data-testid="triage-empty"
                >
                    {data.total === 0
                        ? `Nothing aging past ${agingDays} days. Inbox is healthy.`
                        : `No ${SOURCE_FILTERS.find((s) => s.key === sourceFilter)?.label.toLowerCase()} aged past ${agingDays} days.`}
                </p>
            )}

            {data && visibleItems.length > 0 && (
                <>
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-bl/40">
                        <button
                            type="button"
                            onClick={selectAllVisible}
                            className="text-xs text-bl-muted hover:text-bl flex items-center gap-1.5"
                            data-testid="triage-select-all"
                            title={
                                allVisibleSelected
                                    ? "Deselect everything visible"
                                    : "Select every visible row"
                            }
                        >
                            {allVisibleSelected ? (
                                <CheckSquare className="h-3.5 w-3.5 text-bl-primary" />
                            ) : (
                                <Square className="h-3.5 w-3.5" />
                            )}
                            {allVisibleSelected ? "Deselect all" : "Select all"}
                        </button>
                        <span className="text-[10px] uppercase tracking-wide text-bl-muted ml-auto">
                            {visibleItems.length} of {data.total} shown
                        </span>
                    </div>
                    <ul
                        className="divide-y divide-bl/40"
                        data-testid="triage-list"
                    >
                        {visibleItems.map((item, i) => (
                            <TriageRow
                                key={idOf(item, i)}
                                item={item}
                                index={i}
                                expanded={expandedIds.has(idOf(item, i))}
                                selected={selectedIds.has(idOf(item, i))}
                                busy={busyId === idOf(item, i)}
                                onExpand={() => toggleExpand(idOf(item, i))}
                                onSelect={() => toggleSelect(idOf(item, i))}
                                onMarkShipped={() =>
                                    patchStatus(item, i, "shipped", "Marked shipped")
                                }
                                onMarkReviewed={() =>
                                    patchStatus(item, i, "reviewed", "Marked reviewed")
                                }
                                onArchive={() =>
                                    patchStatus(item, i, "archived", "Archived")
                                }
                                onConvertTicket={() => convertToTicket(item, i)}
                            />
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

function TriageRow({
    item, index, expanded, selected, busy,
    onExpand, onSelect, onMarkShipped, onMarkReviewed, onArchive, onConvertTicket,
}) {
    const id = idOf(item, index);
    const isFeatureReq = item.source_type === "feature_request";
    const fullText = snippet(item);
    const isLong = fullText.length > 110;

    return (
        <li
            className="py-3 flex items-start gap-3"
            data-testid={`triage-item-${id}`}
        >
            <button
                type="button"
                onClick={onSelect}
                className="mt-0.5 shrink-0 p-0.5 -ml-0.5 text-bl-muted hover:text-bl"
                aria-label={selected ? "Deselect" : "Select"}
                data-testid={`triage-select-${id}`}
            >
                {selected ? (
                    <CheckSquare className="h-4 w-4 text-bl-primary" />
                ) : (
                    <Square className="h-4 w-4" />
                )}
            </button>
            {item.severity_hint === "high" ? (
                <AlertCircle className="h-4 w-4 text-[var(--bl-rust)] mt-0.5 shrink-0" />
            ) : (
                <Clock className="h-4 w-4 text-bl-muted mt-0.5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-[var(--bl-stone-soft)] text-bl">
                        {SOURCE_LABEL[item.source_type] || item.source_type}
                    </span>
                    {item.severity_hint === "high" && (
                        <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]">
                            High
                        </span>
                    )}
                    {item.rating != null && (
                        <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-bl-bg text-bl-muted">
                            {item.rating}★
                        </span>
                    )}
                    <span className="text-xs text-bl-muted font-mono">
                        {item.age_days != null ? `${item.age_days}d old` : ""}
                    </span>
                </div>
                <p
                    className={
                        "text-sm text-bl leading-relaxed "
                        + (expanded ? "whitespace-pre-wrap" : "line-clamp-2")
                    }
                    data-testid={`triage-snippet-${id}`}
                >
                    {fullText}
                </p>
                {isLong && (
                    <button
                        type="button"
                        onClick={onExpand}
                        className="mt-1 text-[11px] text-bl-primary hover:underline inline-flex items-center gap-1"
                        data-testid={`triage-expand-${id}`}
                    >
                        {expanded ? (
                            <>
                                <ChevronUp className="h-3 w-3" />
                                Collapse
                            </>
                        ) : (
                            <>
                                <ChevronDown className="h-3 w-3" />
                                Read full
                            </>
                        )}
                    </button>
                )}
                <div className="mt-1 text-xs text-bl-muted">
                    {item.email || item.user_id || "anonymous"}
                    {item.report_id ? ` · ${item.report_id.slice(0, 14)}` : ""}
                </div>
            </div>
            <div
                className="flex items-center gap-1 shrink-0"
                data-testid={`triage-actions-${id}`}
            >
                {item.severity_hint === "high" && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onConvertTicket}
                        className="bl-btn-ghost text-[11px] gap-1 px-2 py-1"
                        title="Escalate to a support ticket (high-severity items)"
                        data-testid={`triage-convert-ticket-${id}`}
                    >
                        <LifeBuoy className="h-3 w-3" />
                        Ticket
                    </button>
                )}
                {isFeatureReq ? (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onMarkShipped}
                        className="bl-btn-ghost text-[11px] gap-1 px-2 py-1"
                        title="Mark feature request as shipped"
                        data-testid={`triage-mark-shipped-${id}`}
                    >
                        <Rocket className="h-3 w-3" />
                        Shipped
                    </button>
                ) : (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onMarkReviewed}
                        className="bl-btn-ghost text-[11px] gap-1 px-2 py-1"
                        title="Mark as reviewed (clear from queue)"
                        data-testid={`triage-mark-reviewed-${id}`}
                    >
                        <Check className="h-3 w-3" />
                        Reviewed
                    </button>
                )}
                <button
                    type="button"
                    disabled={busy}
                    onClick={onArchive}
                    className="bl-btn-ghost text-[11px] gap-1 px-2 py-1"
                    title="Archive (hide from active feedback)"
                    data-testid={`triage-archive-${id}`}
                >
                    {busy ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                        <Archive className="h-3 w-3" />
                    )}
                    Archive
                </button>
            </div>
        </li>
    );
}
